import { AB_TEST_PUBLIC_REGISTRATION_CTA_TEXTS } from 'constants/abTests';
import {
	AFFILIATE_PAGE,
	ESHOPS_LIST,
	GET_MALL_PAY,
	OVERVIEW, PACKAGES,
} from 'constants/routes';

import { FeaturesReady, useFeatureValue } from '@growthbook/growthbook-react';
import useAuthLinksByCountry from 'hooks/useAuthLinksByCountry';
import useRichLink from 'hooks/useRichLink';
import { isLoggedInSelector } from 'modules/auth/selectors';
import React from 'react';
import { useSelector } from 'react-redux';
import { useToggle } from 'react-use';

import { ReactComponent as Avatar } from '@skippay/core/assets/illustrations/avatar.svg';
import { Flex } from '@skippay/core/components/Flex';
import HamburgerMenu from '@skippay/core/components/HamburgerMenu';
import SkeletonPlaceholder from '@skippay/core/components/SkeletonPlaceholder';
import Step from '@skippay/core/components/Step';
import useMediaQuery from '@skippay/core/hooks/useMediaQuery';
import { FormattedMessage, useIntl } from '@skippay/core/i18n/intl';
import { media } from '@skippay/core/styles/media';
import { size } from '@skippay/core/styles/spacing';
import { MENU_MAIN_WEB_PREFIX, getAnalyticsName } from '@skippay/core/utils/analyticsUtils';

import {
	SAvatar,
	SButton,
	SButtonContainer,
	SHamburger,
	SMenu,
	SNavbar,
	STopButton,
} from './index.styled';
import DropdownLinks from '../DropdownLinks';
import NavLink from '../NavLink';
import useLinks from './useLinks';


// https://mallpay.atlassian.net/browse/SS-7558
function useRegistrationButtonText(buttonValue: number) {
	const intl = useIntl();

	switch (buttonValue) {
		case 0:
			return intl.formatMessage({
				id: 'PublicNavbar.login.inMenu.1',
				defaultMessage: 'Chci Skip Pay',
			});
		case 1:
			return intl.formatMessage({
				id: 'PublicNavbar.login.inMenu.2',
				defaultMessage: 'Zkusit Skip Pay',
			});
		case 2:
			return intl.formatMessage({
				id: 'PublicNavbar.login.inMenu.3',
				defaultMessage: 'Vytvořit registraci',
			});
		case 3:
			return intl.formatMessage({
				id: 'PublicNavbar.login.inMenu.4',
				defaultMessage: 'Zaregistrovat se',
			});
		case 4:
			return intl.formatMessage({
				id: 'PublicNavbar.login.inMenu.5',
				defaultMessage: 'To chci',
			});
		default:
			throw new Error(`Unknown button value: ${buttonValue}`);
	}
}

function MobileRegistrationButton(props: { richRegistrationLink: string, registrationLink: string }) {
	const { richRegistrationLink, registrationLink } = props;
	const buttonValue = useFeatureValue<number>(AB_TEST_PUBLIC_REGISTRATION_CTA_TEXTS, 0);

	const registrationButtonText = useRegistrationButtonText(buttonValue);

	return (
		<STopButton
			analyticsId={getAnalyticsName(registrationLink, MENU_MAIN_WEB_PREFIX)}
			testId="registrationAbTestedInMenu"
			to={richRegistrationLink}
			width="auto"
		>
			{registrationButtonText}
		</STopButton>
	);
}

function PublicNavbar(): JSX.Element {
	const showUserNavigation = useSelector(isLoggedInSelector);
	const [showDropdownMenu, toggleDropdownMenu] = useToggle(false);

	const { dropdownLinksAboutUs, dropdownLinksServices } = useLinks();
	const showRegistrationBtns = useMediaQuery(media.lte('tablet'));
	const { loginLink, registrationLink } = useAuthLinksByCountry();

	const richRegistrationLink = useRichLink(registrationLink);

	return (
		<Flex variant="row" align="center" justify="end">
			{showRegistrationBtns && (
				<FeaturesReady
					fallback={<SkeletonPlaceholder width={size(40, true)} height={size(11, true)} />}
					timeout={5000}
				>
					<MobileRegistrationButton richRegistrationLink={richRegistrationLink} registrationLink={registrationLink} />
				</FeaturesReady>
			)}

			<SHamburger>
				<HamburgerMenu onClick={toggleDropdownMenu} isOpen={showDropdownMenu} />
			</SHamburger>

			<SNavbar isOpen={showDropdownMenu}>
				<SMenu>
					<DropdownLinks
						variant="large"
						links={dropdownLinksServices}
						title={<FormattedMessage id="PublicNavbar.whatWeDo" defaultMessage="Naše služby" />}
					/>

					<li>
						<NavLink
							analyticsId={getAnalyticsName(PACKAGES, MENU_MAIN_WEB_PREFIX)}
							testId="navLinkPackages"
							to={PACKAGES}
						>
							<FormattedMessage id="PublicNavbar.packages" defaultMessage="Balíčky" />
						</NavLink>
					</li>

					<li>
						<NavLink
							analyticsId={getAnalyticsName(ESHOPS_LIST, MENU_MAIN_WEB_PREFIX)}
							testId="navLinkEshops"
							to={ESHOPS_LIST}
						>
							<FormattedMessage id="PublicNavbar.whereShopping" defaultMessage="Kde nakupovat" />
						</NavLink>
					</li>

					<li>
						<NavLink
							analyticsId={getAnalyticsName(AFFILIATE_PAGE, MENU_MAIN_WEB_PREFIX)}
							testId="navLinkAffiliate"
							to={AFFILIATE_PAGE}
						>
							<FormattedMessage id="PublicNavbar.affiliate" defaultMessage="Slevy" />
						</NavLink>
					</li>

					<li>
						<NavLink
							analyticsId={getAnalyticsName(GET_MALL_PAY, MENU_MAIN_WEB_PREFIX)}
							testId="navLinkSkippay"
							to={GET_MALL_PAY}
						>
							<FormattedMessage id="PublicNavbar.forEshops" defaultMessage="Pro e-shopy" />
						</NavLink>
					</li>

					<DropdownLinks
						variant="small"
						links={dropdownLinksAboutUs}
						title={<FormattedMessage id="PublicNavbar.aboutUs" defaultMessage="O nás" />}
					/>

					{!showUserNavigation && (
						<SButtonContainer>
							<SButton
								analyticsId={getAnalyticsName(loginLink, MENU_MAIN_WEB_PREFIX)}
								testId="login"
								to={loginLink}
								variant="secondary"
								width="auto"
							>
								<FormattedMessage id="PublicNavbar.login" defaultMessage="Přihlásit se" />
							</SButton>
							<Step h sm />
							<SButton
								analyticsId={getAnalyticsName(registrationLink, MENU_MAIN_WEB_PREFIX)}
								testId="registration"
								to={richRegistrationLink}
								width="auto"
								isLast
							>
								<FormattedMessage id="PublicNavbar.register" defaultMessage="Zaregistovat" />
							</SButton>
						</SButtonContainer>
					)}

					{showUserNavigation && (
						<SButtonContainer single>
							<div className="u-inline-block u-pr">
								<SAvatar>
									<Avatar />
								</SAvatar>

								<SButton
									analyticsId={getAnalyticsName(OVERVIEW, MENU_MAIN_WEB_PREFIX)}
									breakout
									testId="administration"
									to={OVERVIEW}
									width="auto"
									isLast
								>
									<FormattedMessage
										id="PublicNavbar.goToProfile"
										defaultMessage="Moje Skip Pay zóna"
									/>
								</SButton>
							</div>
						</SButtonContainer>
					)}
				</SMenu>
			</SNavbar>
		</Flex>
	);
}

export default PublicNavbar;
